import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { playChime } from "../lib/chime";
import { disablePush, enablePush, isSubscribed, pushPermission, pushSupported, sendPushTest } from "../lib/push";

const KIND_COLORS = {
  "booking.created":    "text-[#d4af37]",
  "booking.assigned":   "text-[#10b981]",
  "booking.completed":  "text-[#10b981]",
  "booking.cancelled":  "text-[#ef4444]",
  "booking.reassigned": "text-[#d4af37]",
  "invoice.sent":       "text-[#a0b2af]",
  "user.signup":        "text-[#34d399]",
};

function formatAgo(iso) {
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return "agora";
  if (m < 60) return `há ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `há ${h}h`;
  const d = Math.floor(h / 24);
  return `há ${d}d`;
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const lastUnreadRef = useRef(0);
  const seenIdsRef = useRef(null); // Set<string> populated on first silent load
  const nav = useNavigate();

  async function load(silent = false) {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 15 } });
      const list = data.items || [];
      setItems(list);
      setUnread(data.unread_count || 0);
      // Play chime if unread count increased (new notification arrived)
      if (!silent && data.unread_count > lastUnreadRef.current && lastUnreadRef.current > 0) {
        playChime({ volume: 0.1 });
      }
      lastUnreadRef.current = data.unread_count || 0;
      // Animated toast for brand-new high-signal notifications (e.g. pending
      // referrals) so admins don't miss them while the PWA is open.
      if (seenIdsRef.current === null) {
        seenIdsRef.current = new Set(list.map(n => n.id));
      } else if (!silent) {
        for (const n of list) {
          if (seenIdsRef.current.has(n.id) || n.read) continue;
          seenIdsRef.current.add(n.id);
          if (n.kind === "referral.pending") {
            toast.success("+1 recomendação pendente", {
              description: n.body,
              duration: 8000,
              action: n.link ? { label: "Ver", onClick: () => nav(n.link) } : undefined,
            });
          }
        }
      }
    } catch {
      /* ignore */
    }
  }

  useEffect(() => {
    load(true);
    const id = setInterval(load, 30000); // poll every 30s
    return () => clearInterval(id);
  }, []);

  // Sync push-subscription status once on mount
  useEffect(() => {
    if (!pushSupported()) return;
    isSubscribed().then(setPushOn).catch(() => {});
  }, []);

  async function togglePush() {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      if (pushOn) {
        await disablePush();
        setPushOn(false);
        toast.success("Notificações push desativadas.");
      } else {
        await enablePush();
        setPushOn(true);
        toast.success("Notificações push ativadas.");
        // Fire a confirmation push so user sees it immediately
        try { await sendPushTest(); } catch { /* ignore */ }
      }
    } catch (e) {
      const msg = e?.message || "Não foi possível ativar";
      if (pushPermission() === "denied") {
        toast.error("Bloqueado nas definições do browser. Ativa nas permissões do site.");
      } else {
        toast.error(msg);
      }
    } finally {
      setPushBusy(false);
    }
  }

  async function onClickItem(n) {
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
        setUnread(u => Math.max(0, u - 1));
        setItems(items => items.map(i => i.id === n.id ? { ...i, read: true } : i));
      } catch { /* ignore */ }
    }
    setOpen(false);
    if (n.link) nav(n.link);
  }

  async function markAllRead() {
    try {
      await api.post("/notifications/read-all");
      setUnread(0);
      setItems(items => items.map(i => ({ ...i, read: true })));
    } catch { /* ignore */ }
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => { setOpen(o => !o); playChime({ volume: 0.08 }); }}
        className="relative p-2 rounded-sm border border-[#1a4640] hover:border-[#d4af37] transition-colors"
        data-testid="notif-bell"
        aria-label="Notificações"
      >
        <Bell size={16} className={unread > 0 ? "text-[#d4af37]" : "text-[#a0b2af]"} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#d4af37] text-[#041f1b] text-[10px] font-bold flex items-center justify-center"
                data-testid="notif-unread-count">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-[22rem] max-h-[28rem] overflow-auto bg-[#062c27] border border-[#1a4640] shadow-2xl z-50" data-testid="notif-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#1a4640] bg-[#041f1b]">
              <div className="eyebrow">Notificações</div>
              <div className="flex items-center gap-3">
                {pushSupported() && (
                  <button
                    onClick={togglePush}
                    disabled={pushBusy}
                    title={pushOn ? "Desativar push" : "Ativar push nativo no dispositivo"}
                    className={`text-[10px] uppercase tracking-widest inline-flex items-center gap-1 transition-colors disabled:opacity-50 ${pushOn ? "text-[#10b981] hover:text-[#34d399]" : "text-[#a0b2af] hover:text-[#d4af37]"}`}
                    data-testid="notif-push-toggle"
                  >
                    {pushOn ? <Bell size={12} /> : <BellOff size={12} />}
                    {pushOn ? "Push on" : "Ativar push"}
                  </button>
                )}
                {unread > 0 && (
                  <button onClick={markAllRead} className="text-[10px] text-[#d4af37] uppercase tracking-widest hover:underline" data-testid="notif-mark-all">
                    Marcar todas lidas
                  </button>
                )}
              </div>
            </div>
            {items.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#a0b2af]">Sem notificações</div>
            ) : (
              <div className="divide-y divide-[#1a4640]">
                {items.map(n => {
                  const colorCls = KIND_COLORS[n.kind] || "text-[#a0b2af]";
                  return (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => onClickItem(n)}
                      className={`w-full text-left p-4 hover:bg-[#0a3a33] transition-colors ${!n.read ? "bg-[#0a3a33]/40" : ""}`}
                      data-testid={`notif-item-${n.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-1">
                          {n.read
                            ? <CheckCircle2 size={14} className="text-[#1a4640]" />
                            : <span className={`inline-block w-2 h-2 rounded-full bg-[#d4af37] mt-1.5`} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`text-xs uppercase tracking-widest ${colorCls}`}>{n.title}</div>
                          <div className="text-sm text-[#fdfbf7] mt-1 leading-snug">{n.body}</div>
                          <div className="text-[10px] text-[#a0b2af] mt-1.5">{formatAgo(n.created_at)}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
